// Customer retail amounts and catalog option IDs only. No supplier cost or markup.
export function selectSetupRule(profile, productId, selected, quantity, today = new Date().toISOString().slice(0, 10)) {
    if (!profile || profile.version !== 1 || profile.product_id !== productId || profile.currency !== 'USD'
        || !/^\d{4}-\d{2}-\d{2}$/.test(profile.valid_until || '') || profile.valid_until < today
        || !Number.isInteger(profile.fee_product_id) || profile.fee_product_id < 1
        || !Number.isInteger(quantity) || quantity < 1 || !Array.isArray(profile.rules) || profile.rules.length > 100) {
        throw new Error('Setup pricing could not be confirmed.');
    }
    const matches = profile.rules.filter(rule => {
        if (!rule || !rule.selectors || typeof rule.selectors !== 'object' || !Object.keys(rule.selectors).length
            || !Number.isInteger(rule.customer_setup_cents) || rule.customer_setup_cents < 0 || rule.customer_setup_cents > 10000000
            || !Number.isInteger(rule.quantity_min) || rule.quantity_min < 1
            || (rule.quantity_max !== null && (!Number.isInteger(rule.quantity_max) || rule.quantity_max < rule.quantity_min))) {
            throw new Error('Invalid setup pricing.');
        }
        return quantity >= rule.quantity_min && (rule.quantity_max === null || quantity <= rule.quantity_max)
            && Object.keys(rule.selectors).every(key => String(selected[key] || '') === String(rule.selectors[key]));
    });
    if (matches.length !== 1) throw new Error('Setup pricing is unavailable for this decoration.');
    const rule = matches[0];
    if (rule.customer_setup_cents > 0 && (!rule.fee_selection || !Number.isInteger(rule.fee_selection.modifier_id)
        || rule.fee_selection.modifier_id < 1 || !Number.isInteger(rule.fee_selection.value_id) || rule.fee_selection.value_id < 1)) {
        throw new Error('Setup pricing option is unavailable.');
    }
    return { ...rule, fee_product_id: profile.fee_product_id };
}

export async function readSetupProfile(productId, token, fetcher = window.fetch.bind(window)) {
    if (!Number.isInteger(productId) || productId < 1 || !token) throw new Error('Setup pricing could not be confirmed.');
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer;
    try {
        const deadline = new Promise((resolve, reject) => {
            timer = setTimeout(() => { if (controller) controller.abort(); reject(new Error('Setup pricing timed out.')); }, 8000);
        });
        const response = await Promise.race([fetcher('/graphql', {
            method: 'POST', credentials: 'same-origin', cache: 'no-store',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            ...(controller ? { signal: controller.signal } : {}),
            body: JSON.stringify({
                query: 'query CheckoutSetup($id: Int!) { site { product(entityId: $id) { entityId metafields(namespace: "canterberry_checkout", keys: ["decoration_setup_v1"], first: 2) { edges { node { key value } } } } } }',
                variables: { id: productId },
            }),
        }), deadline]);
        if (!response.ok) throw new Error('Setup pricing is unavailable.');
        const data = await response.json();
        const product = data.data && data.data.site && data.data.site.product;
        if (data.errors || !product || product.entityId !== productId || !product.metafields || !Array.isArray(product.metafields.edges)) {
            throw new Error('Setup pricing could not be confirmed.');
        }
        const fields = product.metafields.edges.filter(edge => edge.node.key === 'decoration_setup_v1');
        if (!fields.length) return null;
        if (fields.length !== 1 || fields[0].node.value.length > 65535) throw new Error('Invalid setup pricing.');
        const profile = JSON.parse(fields[0].node.value);
        if (!profile || typeof profile !== 'object' || Array.isArray(profile)) throw new Error('Invalid setup pricing.');
        return profile;
    } finally {
        clearTimeout(timer);
    }
}
